import React, { useState, useEffect } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { TECHNOLOGIES_CONFIG, calculateTechUpgradeCost, calculateTechResearchTimeSec } from '@galaxy/shared';
import type { TechId } from '@galaxy/shared';
import { MetalIcon, He3Icon, CreditsIcon } from '../assets/icons.tsx';
import { QuickBoostModal } from './QuickBoostModal.tsx';

export const ResearchView: React.FC = () => {
  const { state, startResearch } = useGame();
  const [researchingId, setResearchingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'military' | 'defense' | 'engineering'>('all');
  const [now, setNow] = useState(Date.now());
  const [quickBoostTarget, setQuickBoostTarget] = useState<{ id: string; name: string; remainingSec: number } | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!state) return null;

  const { research, buildings, planet } = state;
  const currentMetal = planet.resources.metal;
  const currentHe3 = planet.resources.he3;
  const currentCredits = planet.resources.credits;

  const labBuilding = buildings.find((b) => b.type === 'research_lab');
  const labLevel = labBuilding?.level ?? 0;

  const handleStartResearch = async (techId: TechId) => {
    try {
      setResearchingId(techId);
      await startResearch(techId);
    } catch {
      // Handled in store
    } finally {
      setResearchingId(null);
    }
  };

  const techList = Object.values(TECHNOLOGIES_CONFIG).filter(
    (t) => selectedCategory === 'all' || t.category === selectedCategory
  );

  return (
    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Title */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-bright)', letterSpacing: 0.5 }}>
          VIỆN NGHIÊN CỨU KHOA HỌC
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          Cấp độ Viện Nghiên Cứu hiện tại: <span className="mono" style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>CẤP {labLevel}</span>
        </p>
      </div>

      {/* Category Filter Pills */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[
          { id: 'all', label: 'TẤT CẢ' },
          { id: 'military', label: 'QUÂN SỰ' },
          { id: 'defense', label: 'PHÒNG THỦ' },
          { id: 'engineering', label: 'KỸ THUẬT' },
        ].map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedCategory(c.id as any)}
            style={{
              flex: 1,
              padding: '6px 4px',
              borderRadius: 4,
              border: selectedCategory === c.id ? '1px solid var(--accent-cyan)' : '1px solid var(--border-panel)',
              background: selectedCategory === c.id ? 'rgba(0, 229, 255, 0.15)' : 'var(--bg-panel-subtle)',
              color: selectedCategory === c.id ? 'var(--accent-cyan)' : 'var(--text-dim)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--font-main)',
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Tech Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {techList.map((cfg) => {
          const techState = research.find((r) => r.tech_id === cfg.id);
          const currentLevel = techState?.level ?? 0;
          const cost = calculateTechUpgradeCost(cfg.id, currentLevel);
          const timeSec = calculateTechResearchTimeSec(cfg.id, currentLevel);

          const isResearching = techState?.finish_at && techState.finish_at > now;
          const remainingSec = isResearching ? Math.max(0, Math.ceil((techState.finish_at! - now) / 1000)) : 0;

          const meetsLabReq = labLevel >= cfg.lab_level_required;
          const canAfford = currentMetal >= cost.metal && currentHe3 >= cost.he3 && currentCredits >= cost.credits;
          const isMaxLevel = currentLevel >= cfg.max_level;

          return (
            <div key={cfg.id} className="sci-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)' }}>
                    {cfg.name}
                  </h3>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                    Nhánh: {cfg.category === 'military' ? 'Vũ Khí Quân Sự' : cfg.category === 'defense' ? 'Hệ Thống Phòng Thủ' : 'Động Cơ & Kỹ Thuật'}
                  </span>
                </div>

                <span className="mono" style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--accent-amber)',
                  background: 'rgba(255, 179, 0, 0.1)',
                  border: '1px solid rgba(255, 179, 0, 0.3)',
                  padding: '2px 8px',
                  borderRadius: 3,
                }}>
                  CẤP {currentLevel}
                </span>
              </div>

              <p style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.3 }}>
                {cfg.description}
              </p>

              {/* Requirement alert if lab level too low */}
              {!meetsLabReq && (
                <div style={{ fontSize: 11, color: 'var(--status-red)', fontWeight: 600 }}>
                  ⚠️ Yêu cầu Viện Nghiên Cứu tối thiểu Cấp {cfg.lab_level_required} (Hiện có: {labLevel})
                </div>
              )}

              {/* Progress or Actions */}
              {isResearching ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div className="sci-progress" style={{ flex: 1, minWidth: 80 }}>
                    <div className="sci-progress-bar sci-progress-bar-amber" style={{ width: '100%' }} />
                  </div>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--accent-amber)', fontWeight: 700 }}>
                    ⏳ {remainingSec}s
                  </span>
                  <button
                    className="sci-btn"
                    onClick={() => setQuickBoostTarget({ id: cfg.id, name: cfg.name, remainingSec })}
                    style={{
                      padding: '2px 8px',
                      fontSize: 10,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.3) 100%)',
                      color: 'var(--accent-amber)',
                      borderColor: 'var(--accent-amber)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ⚡ TĂNG TỐC
                  </button>
                </div>
              ) : isMaxLevel ? (
                <span style={{ fontSize: 12, color: 'var(--status-green)', fontWeight: 700 }}>
                  ĐÃ ĐẠT CẤP ĐỘ CAO NHẤT
                </span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {/* Costs */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentMetal >= cost.metal ? 'var(--text-bright)' : 'var(--status-red)' }}>
                      <MetalIcon size={12} />
                      <span className="mono">{cost.metal}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentHe3 >= cost.he3 ? 'var(--text-bright)' : 'var(--status-red)' }}>
                      <He3Icon size={12} />
                      <span className="mono">{cost.he3}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: currentCredits >= cost.credits ? 'var(--text-bright)' : 'var(--status-red)' }}>
                      <CreditsIcon size={12} />
                      <span className="mono">{cost.credits}</span>
                    </div>
                    <span className="mono" style={{ color: 'var(--text-dim)' }}>
                      ({timeSec}s)
                    </span>
                  </div>

                  <button
                    className="sci-btn sci-btn-amber"
                    disabled={!meetsLabReq || !canAfford || researchingId === cfg.id}
                    onClick={() => handleStartResearch(cfg.id)}
                    style={{ padding: '6px 12px', fontSize: 12 }}
                  >
                    {researchingId === cfg.id ? 'Đang gửi...' : 'NGHIÊN CỨU'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {quickBoostTarget && (
        <QuickBoostModal
          targetType="research"
          targetId={quickBoostTarget.id}
          targetName={quickBoostTarget.name}
          remainingSeconds={quickBoostTarget.remainingSec}
          onClose={() => setQuickBoostTarget(null)}
          onSuccess={() => setQuickBoostTarget(null)}
        />
      )}
    </div>
  );
};
